import React from 'react';
import { Alert, Box, Button, Stack, Tab, Tabs } from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { PlanningWorkbenchShell } from '../planningcore';
import BudgetValidationCardsPanel from './BudgetValidationCardsPanel';
import BudgetValidationControlGrid from './BudgetValidationControlGrid';
import BudgetValidationArbitrationGrid from './BudgetValidationArbitrationGrid';
import BudgetValidationIssueGrid from './BudgetValidationIssueGrid';
import ValidationDecisionDialog from './ValidationDecisionDialog';
import {
  fetchBudgetValidationWorkbench,
  transitionBudgetValidation,
} from './budgetvalidation.apis';
import {
  BudgetValidationIssueRow,
  BudgetValidationWorkbenchModel,
} from './budgetvalidation.models';

type State = {
  model?: BudgetValidationWorkbenchModel;
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;
  tab: number;
  dialogOpen: boolean;
  actioncode: string;
  defaultTarget?: string;
};
export default class BudgetValidationWorkbench extends React.Component<
  any,
  State
> {
  state: State = {
    loading: false,
    saving: false,
    error: '',
    success: '',
    tab: 0,
    dialogOpen: false,
    actioncode: 'RETURN',
  };
  componentDidMount() {
    void this.load();
  }
  private pci = () =>
    Number(this.props.plancontext?.pci ?? this.props.planningContext?.pci ?? 0);
  private load = async () => {
    const pci = this.pci();
    if (!pci) {
      this.setState({ error: 'Planning Cycle context is missing.' });
      return;
    }
    this.setState({ loading: true, error: '', success: '' });
    try {
      this.setState({ model: await fetchBudgetValidationWorkbench(pci) });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private open = (actioncode: string, defaultTarget?: string) =>
    this.setState({ dialogOpen: true, actioncode, defaultTarget });

  private routeIssue = (row: BudgetValidationIssueRow) =>
    this.open('RETURN', row.targetphasecode);

  // private transition0 = async (
  //   targetphasecode: string,
  //   reason: string,
  //   remarks: string
  // ) => {
  //   const model = this.state.model;
  //   if (!model) return;
  //   this.setState({ saving: true, error: '', success: '' });
  //   try {
  //     await transitionBudgetValidation({
  //       validationid: this.pci(),
  //       actioncode: this.state.actioncode,
  //       targetphasecode,
  //       reason,
  //       remarks,
  //       stamp: model.context.stamp,
  //     });
  //     this.setState({
  //       dialogOpen: false,
  //       success:
  //         this.state.actioncode === 'APPROVE'
  //           ? 'Budget approved and version snapshot created.'
  //           : 'Validation decision recorded.',
  //     });
  //     await this.load();
  //   } catch (e: any) {
  //     this.setState({ error: e?.message || String(e) });
  //   } finally {
  //     this.setState({ saving: false });
  //   }
  // };

  private transition = async (
    targetphasecode: string,
    reason: string,
    remarks: string,
  ) => {
    const model = this.state.model;

    if (!model?.session?.validationid) {
      this.setState({
        error: 'Budget Validation session is unavailable. Refresh and retry.',
      });
      return;
    }

    if (!model.session.stamp) {
      this.setState({
        error: 'Budget Validation concurrency stamp is unavailable. Refresh and retry.',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      await transitionBudgetValidation({
        validationid: model.session.validationid,
        //planningcycleid: this.pci(),
        actioncode: this.state.actioncode,
        targetphasecode,
        reason,
        remarks,
        stamp: model.session.stamp,
      });

      this.setState({
        dialogOpen: false,
        success:
          this.state.actioncode === 'APPROVE'
            ? 'Budget approved and version snapshot created.'
            : 'Validation decision recorded.',
      });

      await this.load();
    }
    catch (e: any) {
      this.setState({
        error: e?.message || String(e),
      });
    }
    finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const m = this.state.model;
    const preview = !m || m.context.mode !== 'ACTIVE';
    const actions = (
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          disabled={!m?.actions.canReturn}
          onClick={() => this.open('RETURN')}
        >
          Return
        </Button>
        <Button
          color="error"
          variant="outlined"
          disabled={!m?.actions.canReject}
          onClick={() => this.open('REJECT')}
        >
          Reject
        </Button>
        <Button
          variant="contained"
          disabled={!m?.actions.canApprove}
          onClick={() => this.open('APPROVE')}
        >
          Approve Budget
        </Button>
      </Stack>
    );
    return (
      <PlanningWorkbenchShell
        {...this.props}
        title="Budget Validation"
        subtitle="Final governance review of consolidation, arbitration and capacity."
        icon={<AccountBalanceOutlinedIcon />}
        loading={this.state.loading}
        saving={this.state.saving}
        error={this.state.error}
        success={this.state.success}
        actions={actions}
        onRefresh={this.load}
      >
        <Box sx={{ p: 3 }}>
          {preview ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Preview Mode.</strong> Consolidation is still in progress.
              All evidence is visible, but final validation actions remain
              disabled until Consolidation is completed.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Validation Active.</strong> Consolidation is complete and
              the budget is ready for a governance decision.
            </Alert>
          )}
          {m && (
            <>
              <BudgetValidationCardsPanel cards={m.cards} />
              <Tabs
                value={this.state.tab}
                onChange={(_, tab) => this.setState({ tab })}
                sx={{ mt: 2, mb: 1 }}
              >
                <Tab label="Capacity" />
                <Tab label={`Arbitration (${m.arbitrations.length})`} />
                <Tab label={`Findings (${m.issues.length})`} />
              </Tabs>
              {this.state.tab === 0 && (
                <BudgetValidationControlGrid
                  rows={m.controlareas}
                  loading={this.state.loading}
                />
              )}{' '}
              {this.state.tab === 1 && (
                <BudgetValidationArbitrationGrid
                  rows={m.arbitrations}
                  loading={this.state.loading}
                />
              )}{' '}
              {this.state.tab === 2 && (
                <BudgetValidationIssueGrid
                  rows={m.issues}
                  loading={this.state.loading}
                  active={!preview}
                  onRoute={this.routeIssue}
                />
              )}
            </>
          )}
          <ValidationDecisionDialog
            open={this.state.dialogOpen}
            actioncode={this.state.actioncode}
            defaultTarget={this.state.defaultTarget}
            onClose={() => this.setState({ dialogOpen: false })}
            onConfirm={this.transition}
          />
        </Box>
      </PlanningWorkbenchShell>
    );
  }
}
